import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AppModal, AppRemixIcon } from '@/layout/AppComponents';
import styled from 'styled-components';
import { Scrollbars } from 'react-custom-scrollbars';
import { useSelector } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import { ProgressBar } from 'react-bootstrap';
import colors from '../../../../../../constants/colors';
import Translator from '../../../../../components/helpers/Translator';
import InformationButtonBlock from './Information/InformationButtonBlock';
import EmailNotificationSettings from '../../EmailNotifications/EmailNotificationSettings';

const InformationTab = (props) => {
  const { taskID } = props;
  const { moduleBridgeHex } = useParams();
  const level1 = useSelector((state) => state.currentPage?.levels?.level1);
  const level2 = useSelector((state) => state.currentPage?.levels?.level2);
  const modulePermissions = useSelector(
    (s) => s.module[moduleBridgeHex]?.permissions?.correctiveActions
  );
  const moduleJsonSettings = useSelector(
    (s) => s.module[moduleBridgeHex].jsonSettings
  );
  const userBridge = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.userBridge
  );
  const [
    showCustomNotificationModal,
    setShowCustomNotificationModal,
  ] = useState(false);

  return (
    <InformationTabContainer>
      <InformationLeft>
        <h3 className="mb-2">
          <span>
            <span
              title={ReactDOMServer.renderToString(
                <Translator
                  file="module-correctiveActions"
                  name="module.ca.maker"
                />
              )}
            >
              {level1?.[taskID]?.userName}
            </span>{' '}
            -{' '}
            <span
              title={ReactDOMServer.renderToString(
                <Translator
                  file="module-correctiveActions"
                  name="module.ca.dateStamp"
                />
              )}
            >
              {level1?.[taskID]?.limitStamp}
            </span>
          </span>
          {level2?.[taskID]?.hasCloseRequest && (
            <CloseRequest>
              <AppRemixIcon icon="close-circle" className="mr-1" />
              <Translator
                file="module-correctiveActions"
                name="module.ca.close-request"
              />
            </CloseRequest>
          )}
        </h3>
        <Scrollbars style={{ height: '385px', paddingRight: '10px' }}>
          <div
            dangerouslySetInnerHTML={{
              __html: level1?.[taskID]?.description,
            }}
          />
        </Scrollbars>
      </InformationLeft>
      <InformationRight>
        <Scrollbars style={{ height: '385px', paddingRight: '10px' }}>
          {userBridge?.permissions?.level !== 2 && (
            <InformationButtonBlock taskID={taskID} />
          )}
          {moduleJsonSettings?.calendarEmail &&
            userBridge?.permissions?.level !== 2 && (
              <>
                <button
                  type="button"
                  className="btn btn-outline-light btn-block btn-sm mb-2"
                  onClick={() => setShowCustomNotificationModal(true)}
                >
                  <Translator
                    file="module-legalCompliance"
                    name="modify.notifications"
                  />
                </button>
                <AppModal
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="modify.notifications"
                    />
                  )}
                  show={showCustomNotificationModal}
                  size="lg"
                  onClose={() => setShowCustomNotificationModal(false)}
                >
                  <EmailNotificationSettings
                    type="Task"
                    element={taskID}
                    onUpdate={() => setShowCustomNotificationModal(false)}
                  />
                </AppModal>
              </>
            )}
          <h4>
            <b>
              <Translator
                file="module-correctiveActions"
                name="module.ca.code"
              />
              :
            </b>{' '}
            {level1?.[taskID]?.code}
          </h4>
          {modulePermissions?.systemCenters && (
            <>
              <h4>
                <b>
                  <Translator
                    file="module-correctiveActions"
                    name="module.ca.systems"
                  />
                  :
                </b>
                <br />
                <br />
                <ul>
                  {level2?.[taskID]?.systems.map((s) => (
                    <li key={s?.id} className="mb-1">
                      {s?.shortName} {s?.name && `(${s?.name})`}
                    </li>
                  ))}
                </ul>
              </h4>
              <h4>
                <b>
                  <Translator
                    file="module-correctiveActions"
                    name="module.ca.centers"
                  />
                  :
                </b>
                <br />
                <br />
                <ul>
                  {level2?.[taskID]?.correctiveActionPlants.map((cap) => (
                    <li key={cap?.id} className="mb-1">
                      {cap?.name}
                    </li>
                  ))}
                </ul>
              </h4>
            </>
          )}
          {modulePermissions?.source && (
            <h4>
              <b>
                <Translator
                  file="module-correctiveActions"
                  name="module.ca.source"
                />
                :
              </b>{' '}
              {level2?.[taskID]?.source}
            </h4>
          )}
          {level1?.[taskID]?.closedUser && (
            <h4>
              <b>
                <Translator
                  file="module-correctiveActions"
                  name="module.ca.closed-user-by"
                />
                :
              </b>{' '}
              <span title={level1?.[taskID]?.closedUser}>
                {level1?.[taskID]?.closedUser}
              </span>
            </h4>
          )}
          {modulePermissions?.taskType && (
            <h4>
              <b>
                <Translator
                  file="module-correctiveActions"
                  name="module.ca.task-type"
                />
                :
              </b>{' '}
              <span title={level2?.[taskID]?.type?.name}>
                {level2?.[taskID]?.type?.shortName}
              </span>
            </h4>
          )}
          {modulePermissions?.responsibles && (
            <h4>
              <b>
                <Translator
                  file="module-correctiveActions"
                  name="module.ca.employee-s"
                />
                :
              </b>
              <br />
              <br />
              <ul>
                {level2?.[taskID]?.responsibles?.map((r) => (
                  <li key={r.id} className="mb-1">
                    {r.name}
                    {r?.responsibles && (
                      <ul>
                        {r?.responsibles.map((sb) => (
                          <li key={sb.id} className="mb-1">
                            {sb.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </h4>
          )}
          <h4
            title={`${ReactDOMServer.renderToString(
              <Translator
                file="module-correctiveActions"
                name={
                  level2?.[taskID]?.progress === 100
                    ? 'module.ca.state-closed'
                    : level2?.[taskID]?.progress > 0
                      ? 'module.ca.state-ongoing'
                      : 'module.ca.state-pending'
                }
              />
            )}: ${level2?.[taskID]?.progress}%`}
          >
            <b>
              <Translator
                file="module-correctiveActions"
                name="module.ca.percent"
              />{' '}
              (
              <Translator
                file="module-correctiveActions"
                name={
                  level2?.[taskID]?.progress === 100
                    ? 'module.ca.state-closed'
                    : level2?.[taskID]?.progress > 0
                      ? 'module.ca.state-ongoing'
                      : 'module.ca.state-pending'
                }
              />
              ) :
            </b>
            <br />
            <br />
            <ProgressBar
              now={
                level2?.[taskID]?.progress === 0
                  ? 10
                  : level2?.[taskID]?.progress
              }
              label={`${level2?.[taskID]?.progress}%`}
              variant={
                level2?.[taskID]?.progress === 100
                  ? 'success'
                  : level2?.[taskID]?.progress > 0
                    ? 'warning'
                    : 'info'
              }
            />
          </h4>
          {level1?.[taskID]?.files?.length > 0 && (
            <>
              <h4>
                <AppRemixIcon icon="attachment-2" className="mr-1" noVariants />
                <b>
                  <Translator file="module-correctiveActions" name="files" />:
                </b>
              </h4>
              {level1?.[taskID]?.files?.map((f) => (
                <FileLink key={f?.id} href={f?.url}>
                  {f?.name}
                </FileLink>
              ))}
            </>
          )}
        </Scrollbars>
      </InformationRight>
    </InformationTabContainer>
  );
};
export default InformationTab;

const InformationTabContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 238px;
  height: 400px;
`;

const InformationLeft = styled.div`
  padding-right: 10px;
  & > h3 {
    display: flex;
    justify-content: space-between;
  }
`;

const InformationRight = styled.div`
  border-left: 1px solid #ddd;
  padding: 10px;
  ul {
    padding-left: 15px;
  }
  & > ul > li > ul {
    margin-bottom: 10px;
  }
`;

const FileLink = styled.a`
  display: block;
  margin-bottom: 5px;
  &,
  &:hover {
    color: #0060df;
  }
`;

const CloseRequest = styled.span`
  display: flex;
  background-color: ${colors.bootstrapWarning};
  color: #000;
  border-radius: 4px;
  padding: 2px 6px;
  font-weight: normal;
`;
